import React, { useState } from 'react';
import { Send, X, AlertCircle } from 'react-feather';

export interface SupportTicketFormProps {
  conversationId: string;
  responseId: string;
  originalQuestion?: string;
  conversationHistory: Array<{
    type: 'user' | 'assistant';
    content: string;
    timestamp: string;
    messageId: string;
    metadata?: any;
  }>;
  onTicketCreated: (ticketInfo: any) => void;
  onCancel: () => void;
  authenticatedUser?: { id: string; name: string; email: string; role: string; orgId?: string } | null;
}

export interface SupportTicketData {
  name: string;
  email: string;
  organization?: string;
  comments: string;
  conversationHistory: any[];
}

/**
 * Support Ticket Form Component
 * Like filling out a distress signal to summon the cavalry
 */
const SupportTicketForm: React.FC<SupportTicketFormProps> = ({
  conversationId,
  responseId,
  originalQuestion,
  conversationHistory,
  onTicketCreated,
  onCancel,
  authenticatedUser
}) => {
  const [formData, setFormData] = useState<SupportTicketData>({
    name: authenticatedUser?.name || '',
    email: authenticatedUser?.email || '',
    organization: authenticatedUser?.orgId || '',
    comments: '',
    conversationHistory: conversationHistory
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof SupportTicketData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Email validation regex - like checking if the map coordinates are valid
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof SupportTicketData, string>> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof SupportTicketData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create the escalation ticket data
      const escalationData = {
        name: formData.name,
        email: formData.email,
        organization: formData.organization,
        comments: formData.comments,
        conversationHistory,
        originalQuestion,
        responseId,
        conversationId
      };

      const response = await fetch('/api/chat/escalate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(escalationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create support ticket');
      }

      const result = await response.json();
      onTicketCreated(result.data);
      
    } catch (error: any) {
      console.error('Failed to submit support ticket:', error);
      setSubmitError(error.message || 'Failed to create support ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Create Support Ticket</h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close form"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              Your conversation history will be included to help our support team assist you better.
              {authenticatedUser && (
                <span className="block mt-1 text-xs text-blue-600">
                  ✓ Contact information auto-filled from your account
                </span>
              )}
            </p>
          </div>
          
          <div className="space-y-1">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors text-sm ${errors.name ? 'border-red-500 bg-red-50' : ''}`}
              placeholder="Your full name"
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="text-xs text-red-600 mt-1">{errors.name}</p>
            )}
          </div>
          
          <div className="space-y-1">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors text-sm ${errors.email ? 'border-red-500 bg-red-50' : ''}`}
              placeholder="your.email@example.com"
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-xs text-red-600 mt-1">{errors.email}</p>
            )}
          </div>
          
          <div className="space-y-1">
            <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
              Organization <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              id="organization"
              name="organization"
              value={formData.organization}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors text-sm"
              placeholder="Your company or organization"
              disabled={isSubmitting}
            />
          </div>
          
          <div className="space-y-1">
            <label htmlFor="comments" className="block text-sm font-medium text-gray-700">
              Additional Information <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="comments"
              name="comments"
              value={formData.comments}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors text-sm resize-none"
              placeholder="Please provide any additional details that might help us assist you..."
              rows={4}
              disabled={isSubmitting}
            />
          </div>
          
          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <span>{submitError}</span>
            </div>
          )}
          
          <div className="flex gap-3 justify-end pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors text-sm font-medium shadow-sm hover:shadow-md"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-eddy-blue text-white rounded-lg hover:bg-eddy-blue/90 transition-colors text-sm font-medium flex items-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating Ticket...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Create Ticket
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportTicketForm;